import PropTypes from 'prop-types';
import {
  flatMap,
  concat,
  isArray,
  flow,
  curry,
  findIndex,
  noop,
  identity,
} from 'lodash';

const CLEAR_SEARCH_TIMER = 1000;
const PAGE_SIZE = 5;
const ITEM_ROOT = 'root';
const ITEM_GROUP_HEADER = 'group-root';
const ITEM_CHILD = 'child';

const findItemIndex = (items, item) =>
  items && findIndex(items, (child) => child.value === item.value);

const isLast = (items, index) => index === items.length - 1;

const hasChildren = ({ children }) => children && children.length > 0;

const isLastComparator = (options, option) =>
  isLast(options, findItemIndex(options, option));

const findItemBy = (options, option, comparator) =>
  hasChildren({
    children: options.filter(
      ({ children }) => children && comparator(children, option)
    ),
  });

const isGroupRoot = (name) => name && name.indexOf(ITEM_GROUP_HEADER) > -1;

const groupOption = (option, hasOptionChildren) => {
  const { children } = option;

  const optionItem = {
    ...option,
    tier: hasOptionChildren ? ITEM_GROUP_HEADER : ITEM_ROOT,
    children: hasOptionChildren ? children : null,
  };

  if (!hasOptionChildren) {
    return optionItem;
  }

  return concat(
    optionItem,
    children.map((child) => ({
      tier: ITEM_CHILD,
      value: child.value,
      label: child.label,
    }))
  );
};

const normalizeOptions = ({ defaultLabel, options }) => {
  let normalizedOptions = isArray(options)
    ? [...options]
    : Object.keys(options).map((value) => ({
        value,
        label: options[value],
      }));

  normalizedOptions = flatMap(normalizedOptions, (option) => {
    const curriedGroupOptions = curry(groupOption);
    const getGroupOptions = flow(
      hasChildren,
      curriedGroupOptions(option)
    );
    return getGroupOptions(option);
  });

  if (!defaultLabel) {
    return normalizedOptions;
  }

  return [
    {
      value: '',
      label: defaultLabel,
    },
    ...normalizedOptions,
  ];
};

const isLastOption = (options, option) =>
  findItemBy(options, option, isLastComparator);

const getSelectedOption = (options, selected) =>
  options.reduce((selectedOptionLabel, option) => {
    const { children, tier, value, label } = option;
    if (isGroupRoot(tier)) {
      const foundItemIndex =
        children && findItemIndex(children, { value: selected });
      if (foundItemIndex > 0) {
        // eslint-disable-next-line no-param-reassign
        selectedOptionLabel = children[foundItemIndex].label;
      }
    } else if (value === selected) {
      // eslint-disable-next-line no-param-reassign
      selectedOptionLabel = label;
    }
    return selectedOptionLabel;
  }, undefined);

const getSelectedText = ({
  options,
  selected,
  defaultLabel,
  formatInputLabel,
}) => {
  const byValue = ({ value }) => value === selected;
  const filteredOptions = options.filter(byValue);
  const option = filteredOptions[0] || {};
  const rawInputLabel = option.label;
  const formatFunction = formatInputLabel || identity;

  return rawInputLabel ? formatFunction(rawInputLabel) : defaultLabel || '';
};

export const DropdownPropTypes = {
  options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  onChange: PropTypes.func,
  renderOption: PropTypes.func,
  renderSelectedOption: PropTypes.func,
  fullWidth: PropTypes.bool,
  defaultLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  formatInputLabel: PropTypes.func,
  disabled: PropTypes.bool,
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  testID: PropTypes.string,
  label: PropTypes.string,
  error: PropTypes.bool,
  success: PropTypes.bool,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['error', 'success', 'info']),
      message: PropTypes.string,
    })
  ),
};

export const DropdownDefaultProps = {
  options: [],
  onChange: noop,
  renderOption: undefined,
  renderSelectedOption: undefined,
  fullWidth: false,
  defaultLabel: 'Select...',
  formatInputLabel: undefined,
  disabled: false,
  selected: undefined,
  size: 'medium',
  testID: undefined,
  label: undefined,
  error: false,
  success: false,
  messages: undefined,
};

export {
  CLEAR_SEARCH_TIMER,
  PAGE_SIZE,
  ITEM_ROOT,
  ITEM_GROUP_HEADER,
  ITEM_CHILD,
  normalizeOptions,
  isGroupRoot,
  isLastOption,
  getSelectedOption,
  getSelectedText,
};
